import { Box } from '@chakra-ui/react';
import { useEffect, useId, useState } from 'react';

// Mermaid sizes each node by laying out its label and shrinking the box to fit
// the measured text. Two things make that measurement disagree with what the
// browser finally paints — and the gap shows up as labels clipped on the side
// or bottom:
//   1. Font family mismatch. Mermaid measures with its default `"trebuchet ms"`
//      while this SVG is injected into a container that inherits `longform`
//      (Lato). Lato's glyphs are wider/taller than the box mermaid drew.
//   2. Late-loading web font. The render runs in an effect right after mount,
//      often before Lato has loaded, so mermaid measures against a narrower
//      fallback and the real glyphs overflow once Lato swaps in.
// Pin mermaid's font to the family the diagram actually renders in, and wait
// for that font to load before measuring, so the box and the glyphs agree.
const MERMAID_FONT = 'Lato, sans-serif';
export const MermaidDiagram = ({
  code
}: {
  code: string;
}) => {
  const id = useId().replace(/:/g, '');
  const [svg, setSvg] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    const renderDiagram = async () => {
      try {
        const {
          default: mermaid
        } = await import('mermaid');

        // Don't measure text until the render font is loaded, otherwise nodes
        // get sized against a fallback font and clip when Lato swaps in.
        if (typeof document !== 'undefined' && document.fonts?.ready) {
          await document.fonts.ready;
        }
        if (cancelled) {
          return;
        }
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          fontFamily: MERMAID_FONT,
          themeVariables: {
            fontFamily: MERMAID_FONT
          },
          flowchart: {
            htmlLabels: true,
            useMaxWidth: true
          }
        });
        const {
          svg: svg_0
        } = await mermaid.render(`mermaid-${id}`, code);
        if (!cancelled) {
          setSvg(svg_0);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message ?? 'Mermaid render error');
        }
      }
    };
    renderDiagram();
    return () => {
      cancelled = true;
    };
  }, [code, id]);
  if (error) {
    return <Box as={'pre'} borderRadius={'md'} borderWidth={1} color={'red.500'} fontSize={'sm'} p={2}>
        {error}
      </Box>;
  }
  return <Box dangerouslySetInnerHTML={{
    __html: svg
  }} fontSize={'16px'} marginBottom={8} css={{
    // Scale the diagram to the container without cropping it.
    '& svg': {
      maxWidth: '100%',
      height: 'auto',
      display: 'block'
    },
    // foreignObject labels default to clipping their contents; let them
    // show fully so a glyph descender or extra wrap line is never cut off.
    '& foreignObject': {
      overflow: 'visible'
    },
    // Force the rendered labels onto the same font mermaid measured with,
    // and stop inherited typography (line-height: 1.8 etc.) from re-flowing
    // them taller than the box mermaid sized.
    '& .nodeLabel, & .edgeLabel, & .label, & foreignObject div, & foreignObject span': {
      fontFamily: MERMAID_FONT,
      lineHeight: 'normal'
    }
  }} />;
};